import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { NetworkStatus, useQuery } from "@apollo/client";
import { GridPaginationModel } from "@mui/x-data-grid";
import dayjs from "dayjs";

import {
  GetTimestreamDataQuery,
  GetTimestreamDataQueryVariables,
  ModelType,
} from "../../../../../API";
import { useDateRange } from "../../../../../common/components/datePicker/modelsDatePickers";
import { useCustomerIdGuard } from "../../../../../common/hooks/useCustomerIdGuard";
import { GET_TIMESTREAM_DATA } from "../../../../../common/operations/queries";
import { convertDateToUTC } from "../../../../../common/utils/timestampUtils";
import {
  DEFAULT_EVENTS_LIMIT,
  EVENTS_DATE_FORMAT,
} from "../../../../data-hub/components/models/common/events.constants";
import { LoadMoreType } from "../../../../data-hub/components/models/common/events.models";
import { useEventsFilters } from "../../../../data-hub/components/models/common/hooks/useEventsFilters";

const normalizeCameraId = (id?: string): string => {
  if (!id) return "";

  return id.startsWith("DE#") ? id.slice(3) : id;
};

export const useGetSecurityEventsServer = (pageSize?: number) => {
  const selectedCustomerId = useCustomerIdGuard();
  const { model } = useParams();
  const dates = useDateRange();
  let queryInputOffset = 0;

  // Default to last 7 days for People & Vehicle when no dates are selected
  const isPeopleAndVehicle = model === ModelType.peopleandvehicle;

  // Compute a stable default 7-day range only once (prevents pagination reset)
  const defaultRangeRef = useRef<{ startDate: string; endDate: string } | null>(
    null
  );

  if (
    isPeopleAndVehicle &&
    !dates?.from &&
    !dates?.to &&
    !defaultRangeRef.current
  ) {
    const defaultStart = dayjs(
      convertDateToUTC(dayjs().subtract(7, "day").toDate())
    ).format(EVENTS_DATE_FORMAT);
    const defaultEnd = dayjs(convertDateToUTC(new Date())).format(
      EVENTS_DATE_FORMAT
    );

    defaultRangeRef.current = { startDate: defaultStart, endDate: defaultEnd };
  }

  const startDate = dates?.from
    ? dayjs(convertDateToUTC(dates.from)).format(EVENTS_DATE_FORMAT)
    : isPeopleAndVehicle
    ? defaultRangeRef.current?.startDate ?? null
    : null;
  const endDate = dates?.to
    ? dayjs(convertDateToUTC(dates.to)).format(EVENTS_DATE_FORMAT)
    : isPeopleAndVehicle
    ? defaultRangeRef.current?.endDate ?? null
    : null;

  const { locationVariable, tagLocationsVariable, cameraVariable } =
    useEventsFilters();

  const locationId = locationVariable?.value ?? "";
  const selectedCameraId = cameraVariable?.value ?? "";

  let locations: Array<string> | null = [];

  if (locationId) {
    locations = [locationId ?? ""];
  } else if (tagLocationsVariable) {
    locations = tagLocationsVariable
      .map(l => l?.value ?? "")
      .filter(f => f !== "");

    if (locations.length === 0) {
      locations = null;
    }
  }

  const { data, loading, fetchMore, networkStatus } = useQuery<
    GetTimestreamDataQuery,
    GetTimestreamDataQueryVariables
  >(GET_TIMESTREAM_DATA, {
    skip: !selectedCustomerId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
    variables: {
      input: {
        customerId: selectedCustomerId,
        model: ModelType.security,
        dateRange: JSON.stringify({ startDate, endDate }),
        locations,
        offset: 0,
        limit: pageSize ?? DEFAULT_EVENTS_LIMIT,
        nextToken: null,
      },
    },
  });

  const loadMore: LoadMoreType = async (
    offset: number,
    limit: number,
    nextToken?: string | null
  ) => {
    queryInputOffset = offset;

    return await fetchMore({
      variables: {
        input: {
          customerId: selectedCustomerId,
          model: ModelType.security,
          dateRange: JSON.stringify({ startDate, endDate }),
          locations,
          offset,
          limit,
          nextToken,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return { getTimestreamData: fetchMoreResult.getTimestreamData };
      },
    });
  };

  const parsedQueryResult = useMemo(() => {
    try {
      return data?.getTimestreamData
        ? JSON.parse(data.getTimestreamData as string)
        : [];
    } catch (error) {
      console.error("Error parsing query result", error);

      return [];
    }
  }, [data]);

  const filteredBody = useMemo(() => {
    const body = parsedQueryResult?.body ?? [];
    const wanted = normalizeCameraId(selectedCameraId);

    if (!wanted) return body;

    type SecurityEventBodyItem = { camera_id?: string | null };

    return body.filter(
      (item: SecurityEventBodyItem) =>
        normalizeCameraId(item?.camera_id ?? "") === wanted
    );
  }, [parsedQueryResult, selectedCameraId]);

  const nextToken = parsedQueryResult?.nextToken;

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: pageSize ?? DEFAULT_EVENTS_LIMIT,
  });

  const [filters, setFilters] = useState({
    startDate,
    endDate,
    locations,
    selectedCameraId,
  });

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    const isPageSizeChange = model.pageSize !== paginationModel.pageSize;
    const page = isPageSizeChange ? 0 : model.page;

    if (loadMore) {
      const offset = page * model.pageSize;

      queryInputOffset = offset;

      const effectiveNextToken = page === 0 ? null : nextToken;

      loadMore(offset, model.pageSize, effectiveNextToken).then(() => {
        setPaginationModel(prev => ({
          ...prev,
          page,
          pageSize: model.pageSize,
        }));
      });
    }
  };

  useEffect(() => {
    const locationsFilterChanged =
      filters.locations?.toString() !== locations?.toString();

    if (
      startDate !== filters.startDate ||
      endDate !== filters.endDate ||
      locationsFilterChanged ||
      selectedCameraId !== filters.selectedCameraId
    ) {
      setPaginationModel({
        page: 0,
        pageSize: pageSize ?? DEFAULT_EVENTS_LIMIT,
      });

      // Always fetch the first page after filters change to avoid stale last page
      const firstPageSize = pageSize ?? DEFAULT_EVENTS_LIMIT;

      loadMore(0, firstPageSize, null);

      setFilters(prevState => {
        return {
          ...prevState,
          startDate,
          endDate,
          locations,
          selectedCameraId,
        };
      });
    }
  }, [startDate, endDate, locationId, selectedCameraId]);

  const hasNextPage = useMemo(() => {
    return nextToken != null;
  }, [nextToken]);

  const isActivePageChange = useMemo(() => {
    return hasNextPage && !loading;
  }, [hasNextPage, loading]);

  const queryInput = useMemo(() => {
    return {
      input: {
        customerId: selectedCustomerId,
        model: model ?? "",
        dateRange: JSON.stringify({ startDate, endDate }),
        locations,
        offset: queryInputOffset,
        limit: pageSize ?? DEFAULT_EVENTS_LIMIT,
        nextToken,
      },
    };
  }, [
    selectedCustomerId,
    startDate,
    endDate,
    locations,
    queryInputOffset,
    pageSize,
    nextToken,
  ]);

  return {
    parsedResult: filteredBody,
    nextToken,
    loading: loading || networkStatus === NetworkStatus.refetch,
    loadMore,

    paginationModel,
    handlePaginationModelChange,
    hasNextPage,
    isActivePageChange,
    queryInput,
  } as const;
};
